import axios from 'axios';

// const API_URL = 'http://localhost:8000/api';

// const API_URL = 'http://localhost:8000/api';
// const API_URL = process.env.REACT_APP_API_BASE_URL ? `${process.env.REACT_APP_API_BASE_URL}/api` : 'http://localhost:8000/api';

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

const API_URL = process.env.NODE_ENV === 'production' 
  ? 'https://eatsy.pro/api'
  : 'http://localhost:8000/api';


export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Add this line
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Token ${token}`;
  }
  // Add this line to get CSRF token from cookie
  config.headers['X-CSRFToken'] = getCookie('csrftoken') || '';
  return config;
}, (error) => {
  return Promise.reject(error);
});


api.interceptors.response.use((response) => {
  // console.log('API Response:', response);
  return response;
}, (error) => {
  console.error('API Error:', error.response);
  return Promise.reject(error);
});

export interface Publication {
  id: number;
  name: string;
  description: string;
  uses_ranking: boolean;
  uses_michelin_stars: boolean;
  venue_type: 'BOTH' | 'BAR' | 'RESTAURANT';
}

export interface YearlyRating {
  id: number;
  publication: {
    id: number;
    name: string;
    uses_michelin_stars: boolean;
    uses_ranking: boolean;
  };
  year: number;
  michelin_stars: number | null;
  ranking: number | null;
  rating: number | null;
}

export interface Venue {
  id: number;
  name: string;
  description: string;
  image: string;
  proxied_image?: string;
  venue_type: 'BAR' | 'RESTAURANT';
  country: string;
  city: string;
  yearly_ratings: YearlyRating[];
  /** True when we scrape this venue's bookings, so an alert can actually fire. */
  tracks_availability?: boolean;
}

export interface UserProfile {
  id: number;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    is_superuser: boolean;
    is_staff?: boolean;
  };
  visited_venues: VenueWithVisitInfo[];
  /** Venues this user gets new-table emails for. */
  alert_venue_ids?: number[];
  googleFirstName?: string;
  is_pro: boolean;
  pro_since: string | null;
  email_consent?: boolean;
  email_consent_shown?: boolean;
}

export interface City {
  id: number;
  name: string;
  image_url: string;
  proxied_image?: string;
}

export interface VisitInfo {
  visited_date: string;
}

export interface VenueWithVisitInfo extends Venue {
  visit_info?: VisitInfo;
}

export const fetchVenues = async (publication?: string, year?: number): Promise<Venue[]> => {
  const params: any = {};
  if (publication) params.publication = publication;
  if (year) params.year = year;
  const response = await api.get('/venues/', { params });
  return response.data;
};

export const fetchTopBars = async (): Promise<Venue[]> => {
  const response = await api.get('/venues/top_bars/');
  return response.data;
};

export const fetchTopRestaurants = async (): Promise<Venue[]> => {
  const response = await api.get('/venues/top_restaurants/');
  return response.data;
};

export const toggleVisit = async (venueId: number, date: Date | null): Promise<UserProfile> => {
  try {
    const response = await api.post(`/venues/${venueId}/toggle_visit/`, {
      visited_date: date ? date.toISOString().split('T')[0] : null,
    });
    return response.data as UserProfile;
  } catch (error) {
    console.error('Error in toggleVisit:', error);
    throw error;
  }
};

export const toggleAlert = async (venueId: number, enabled?: boolean): Promise<UserProfile> => {
  const response = await api.post(`/venues/${venueId}/toggle_alert/`, { enabled });
  return response.data as UserProfile;
};

export const sendTestAlertEmail = async (): Promise<{ detail: string }> => {
  const response = await api.post('/user-profiles/test-alert-email/');
  return response.data;
};

export const fetchUserProfile = async () => {
  const response = await api.get('/user-profiles/me/');
  const googleFirstName = localStorage.getItem('googleFirstName');
  return {
    ...response.data,
    googleFirstName
  };
};

export const fetchAllVenues = async (): Promise<Venue[]> => {
  const response = await api.get('/venues/');
  return response.data;
};

export const fetchVenuesByCity = async (city: string): Promise<Venue[]> => {
  try {
    const response = await api.get('/venues/');
    const filteredVenues = response.data.filter((venue: Venue) => 
      venue.city.toLowerCase() === city.toLowerCase()
    );
    return filteredVenues;
  } catch (error) {
    console.error('Error fetching venues:', error);
    throw error;
  }
};

export const fetchAllCities = async (): Promise<City[]> => {
  const response = await api.get('/cities/');
  return response.data;
};

export const fetchPublications = async (): Promise<Publication[]> => {
  const response = await api.get('/publications/');
  return response.data;
};

export const authenticateWithGoogle = async (accessToken: string) => {
  const response = await api.post('/auth/google/', { access_token: accessToken });
  const authToken = response.data.key;
  const googleFirstName = response.data.user.user.first_name;
  
  localStorage.setItem('token', authToken);
  localStorage.setItem('googleFirstName', googleFirstName);
  
  const profileResponse = await api.get('/user-profiles/me/');
  
  return {
    token: authToken,
    profile: profileResponse.data,
    googleFirstName
  };
};

export const deleteUserAccount = async (): Promise<void> => {
  await api.delete('/user-profiles/delete_account/');
};

export interface UserStats {
  total_users: number;
  active_users_30d: number;
  new_users_30d: number;
  /** Everyone who has ever bought PRO. Only goes up - this is the revenue figure. */
  pro_purchases: number;
  /** Purchases whose fixed access window has not lapsed yet. */
  pro_active: number;
  total_visits: number;
  visits_30d: number;
  /** Users who joined before the charted window, so growth does not restart at zero. */
  baseline_users: number;
  generated_at: string;
  /** Zero-filled: a month with no signups is present with 0, not missing. */
  monthly: Array<{
    month: string;
    signups: number;
    pro: number;
  }>;
}

export const fetchUserStats = async (): Promise<UserStats> => {
  const response = await api.get('/user-profiles/statistics/');
  return response.data;
};

// ---------------------------------------------------------------------------
// PRO access administration (admin only)
// ---------------------------------------------------------------------------

export interface ProAccessUser {
  id: number;
  username: string;
  email: string;
  full_name: string;
  is_pro: boolean;
  /** The one that gates paid features: bought AND not lapsed. */
  is_pro_active: boolean;
  pro_never_expires: boolean;
  pro_since: string | null;
  pro_expires_at: string | null;
  pro_days_remaining: number | null;
  /** Days an admin has added (or removed, if negative). */
  pro_extra_days: number;
  date_joined: string;
  last_login: string | null;
  last_accessed_at: string | null;
}

export interface ProAccessResponse {
  count: number;
  /** The standard window length, so the UI never hardcodes it. */
  access_days: number;
  summary: Record<ProAccessStanding, number>;
  standing: ProAccessStanding | 'all';
  ordering: ProAccessOrdering;
  results: ProAccessUser[];
}

export type ProAccessStanding = 'unlimited' | 'active' | 'lapsed' | 'never';
export type ProAccessOrdering = 'access' | 'newest' | 'oldest' | 'recent_login' | 'oldest_login' | 'recent_access' | 'oldest_access' | 'name';

export type ProAccessAction =
  | { action: 'extend'; days: number }
  | { action: 'grant' }
  | { action: 'revoke' }
  | { action: 'set_never_expires'; value: boolean };

export const fetchProAccess = async (filters: {
  query?: string;
  standing?: ProAccessStanding | 'all';
  ordering?: ProAccessOrdering;
} = {}): Promise<ProAccessResponse> => {
  const response = await api.get<ProAccessResponse>('/user-profiles/pro-access/', {
    params: {
      ...(filters.query ? { q: filters.query } : {}),
      ...(filters.standing && filters.standing !== 'all' ? { standing: filters.standing } : {}),
      ...(filters.ordering ? { ordering: filters.ordering } : {}),
    },
  });
  return response.data;
};

export const adjustProAccess = async (
  profileId: number,
  payload: ProAccessAction,
): Promise<ProAccessUser> => {
  const response = await api.post<ProAccessUser>(
    `/user-profiles/${profileId}/pro-access/`, payload,
  );
  return response.data;
};

// ---------------------------------------------------------------------------
// Scraper status (admin only)
// ---------------------------------------------------------------------------

export type ScraperStatus =
  | 'running'
  | 'healthy'
  | 'degraded'
  | 'no_availability'
  | 'unavailable'
  | 'stale'
  | 'never_checked'
  | 'error'
  | 'paused'
  | 'off_list';

export interface ScraperVenueStatus {
  id: string;
  venue_id: number;
  venue: string;
  city: string | null;
  image: string | null;
  proxied_image: string | null;
  provider: string;
  provider_label: string;
  slug: string;
  is_active: boolean;
  status: ScraperStatus;
  status_label: string;
  slots: number;
  bookable: number;
  requestable: number;
  by_party: Array<{ party_size: number; slots: number }>;
  days: Array<{ date: string; slots: number }>;
  running_since: string | null;
  running_for_seconds: number | null;
  last_checked_at: string | null;
  last_checked_age_seconds: number | null;
  last_duration_ms: number | null;
  last_check_status: string | null;
  checks_recorded: number;
  success_rate: number | null;
  consecutive_failures: number;
  last_party_size: number | null;
  last_error: string | null;
  unavailable_reason: string | null;
  unavailable_minutes_ago: number | null;
  /** Other providers serving this venue's availability right now. */
  covered_by: string[];
}

export interface ScraperProviderStatus {
  /** A provider name, or 'independent' for the grouped single-venue scrapers. */
  name: string;
  label: string;
  /** True for the card covering restaurants that run their own booking system. */
  independent: boolean;
  /** Provider names this card covers - one, or several for 'independent'. */
  providers: string[];
  /** Their display names, for showing what the group contains. */
  members: string[];
  requires_browser: boolean | null;
  uses_proxy: boolean | null;
  sources: number;
  slots: number;
  healthy: number;
  errors: number;
  /** Unique restaurants attributed to this provider after choosing their best source. */
  restaurants: number;
  healthy_restaurants: number;
  restaurant_errors: number;
  restaurant_slots: number;
  avg_duration_ms: number | null;
  last_checked_age_seconds: number | null;
}

export interface ScraperExecution {
  run_id: string;
  request_id: string | null;
  party_size: number;
  status: 'starting' | 'running' | 'finishing' | 'completed' | 'completed_with_errors' | 'failed';
  started_at: string;
  finished_at: string | null;
  venues_done: number;
  venues_total: number;
  slots: number;
  errors: number;
  message: string;
}

export interface ScraperActiveRun {
  phase?: 'starting' | 'running' | 'finishing';
  run_id: string;
  started_at: string | null;
  elapsed_seconds: number | null;
  party_sizes: number[];
  venues_in_flight: number;
  venues_done: number;
  venues_total: number;
  slots_so_far: number;
  errors_so_far: number;
  now_checking: Array<{ provider: string; slug: string; for_seconds: number | null }>;
}

export interface ScraperAttentionItem {
  id: string;
  venue: string;
  provider: string;
  slug: string;
  severity: 'high' | 'medium' | 'low';
  issue: string;
  detail: string;
  action: string;
}

export interface ScraperRun {
  run_id: string;
  started_at: string | null;
  finished_at: string | null;
  venues: number;
  slots: number;
  errors: number;
  providers: string[];
  party_sizes: number[];
  wall_seconds: number | null;
  age_seconds: number | null;
}

export interface ScraperQueuedRun {
  request_id: string;
  worker_mode: 'local' | 'cron';
  /** Null only if the request file held something unexpected. */
  party_size: number | null;
  queued_at: string;
  waiting_seconds: number;
}

export interface ScraperStatusResponse {
  generated_at: string;
  database: { path: string; exists: boolean; size_bytes: number };
  totals: {
    sources: number;
    active_sources: number;
    /** Sources a run would actually check: excludes off-list and paused. */
    scraped_sources: number;
    /** Unique restaurants represented by the scraped sources. */
    scraped_venues: number;
    /** Venues those sources cover - a venue on two sites counts once. */
    covered_venues: number;
    /** Every restaurant on the current guide, including any with no source at all. */
    on_list_venues: number;
    venues: number;
    slots: number;
    bookable: number;
    requestable: number;
    by_party: Array<{ party_size: number; slots: number }>;
    by_status: Partial<Record<ScraperStatus, number>>;
    healthy: number;
    /** Restaurant-level status counts; one best source per restaurant. */
    by_venue_status: Partial<Record<ScraperStatus, number>>;
    healthy_venues: number;
    venue_needs_attention: number;
    needs_attention: number;
    freshest_check_seconds: number | null;
    oldest_check_seconds: number | null;
  };
  active_run: ScraperActiveRun | null;
  execution: ScraperExecution | null;
  worker_mode: 'local' | 'cron';
  /** A Run press waiting for production cron or the local worker. */
  queued_run: ScraperQueuedRun | null;
  attention: ScraperAttentionItem[];
  providers: ScraperProviderStatus[];
  venues: ScraperVenueStatus[];
  timeline: Array<{ date: string; total: number; [provider: string]: number | string }>;
  recent_runs: ScraperRun[];
}

export interface ScraperRunTriggerResult {
  status: 'queued';
  queued_run: ScraperQueuedRun;
  started: boolean;
  party_size: number;
  message: string;
}

/** Queue a scrape. Production cron or the local worker consumes the request. Staff only. */
export const triggerScraperRun = async (
  partySize: number
): Promise<ScraperRunTriggerResult> => {
  const response = await api.post<ScraperRunTriggerResult>(
    '/scraper-status/run/',
    { party_size: partySize }
  );
  return response.data;
};

export const retryScraperWorker = async () => {
  const response = await api.post('/scraper-status/retry-worker/');
  return response.data as { message: string };
};

export const fetchScraperStatus = async (): Promise<ScraperStatusResponse> => {
  const response = await api.get<ScraperStatusResponse>('/scraper-status/');
  return response.data;
};

/**
 * Whether a user can reach the admin tooling.
 *
 * The admin APIs use DRF's IsAdminUser, which checks `is_staff`, so that is the
 * flag that decides. `is_superuser` is accepted too because Django superusers
 * created before `is_staff` was exposed may not have it set, and because a
 * superuser can always reach these APIs anyway.
 *
 * Checking only `is_superuser` (as this used to) meant the two sides could
 * disagree: a staff-but-not-superuser admin saw no links and got redirected,
 * while a superuser-but-not-staff one saw the page then got a 403 from the API.
 */
export const isAdminUser = (profile: UserProfile | null): boolean =>
  Boolean(profile?.user?.is_staff || profile?.user?.is_superuser);
